import React, { useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { customDateFormat } from 'helpers/dateFormat';
import errorIcon from 'assets/error_24px.svg';
import RetryLoadsModal from '../RetryLoadsModal';

const failedBannerDefaultPropOnDismiss = () => {};
const FailedBanner = ({
  item,
  onRetry,
  onDismiss = failedBannerDefaultPropOnDismiss
}) => {
  const bannerRef = useRef();
  const [showRetryModal, setShowRetryModal] = useState(false);
  const getMessage = useCallback(() => {
    const cropName = item.otherFields?.cropName || '';
    const yieldName = item.otherFields?.yieldName || '';
    const formattedDate = customDateFormat(
      item.createdAt,
      'MMM d, yyyy hh:mma'
    );

    if (item.actionType === 'DELETE') {
      return `Unable to delete ${cropName} load ${yieldName} (submitted ${formattedDate}).`;
    }

    if (item.actionType === 'EDIT') {
      return `Unable to save edits for ${cropName} load ${yieldName} (submitted ${formattedDate}).`;
    }

    // Default message for 'ADD' or any other action type
    return `Unable to save the ${cropName} loads that were added on ${formattedDate}.`;
  }, [item]);

  const handleDismiss = e => {
    e.preventDefault();
    e.stopPropagation();
    e.target.blur();
    bannerRef.current.classList.add('completed-banner-dismiss-animation');
    bannerRef.current.addEventListener('animationend', onDismiss, {
      once: true
    });
  };

  const getButtonConfig = () => {
    if (item.actionType === 'DELETE' || item.actionType === 'EDIT') {
      return {
        label: 'Dismiss',
        onClick: handleDismiss,
        testId: 'dismiss-button'
      };
    }

    // Default for 'ADD' or any other action type
    return {
      label: 'Try Again',
      onClick: () => setShowRetryModal(true),
      testId: 'retry-button'
    };
  };

  const handleRetryConfirm = () => {
    setShowRetryModal(false);
    bannerRef.current.classList.add('completed-banner-dismiss-animation');
    bannerRef.current.addEventListener(
      'animationend',
      () => {
        onRetry();
      },
      { once: true }
    );
  };

  const handleDiscardConfirm = () => {
    setShowRetryModal(false);
    bannerRef.current.classList.add('completed-banner-dismiss-animation');
    bannerRef.current.addEventListener(
      'animationend',
      () => {
        onDismiss();
      },
      { once: true }
    );
  };

  const buttonConfig = getButtonConfig();

  return (
    <>
      <div
        ref={bannerRef}
        key={`failed-${item.id}`}
        className="text-sm bg-red-100 flex flex-row items-center justify-between banner-container"
      >
        <p className="inline-flex items-center">
          <img
            alt="error"
            src={errorIcon}
            className="
            mr-2 h-24px w-24px
          "
          />
          {getMessage()}
        </p>
        <button
          type="button"
          className="btn-action-task"
          data-testid={buttonConfig.testId}
          onClick={buttonConfig.onClick}
        >
          {buttonConfig.label}
        </button>
      </div>
      <RetryLoadsModal
        open={showRetryModal}
        onClose={() => setShowRetryModal(false)}
        onDiscard={handleDiscardConfirm}
        onRetry={handleRetryConfirm}
        taskData={item}
      />
    </>
  );
};

FailedBanner.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    createdAt: PropTypes.string,
    cropSeasonId: PropTypes.string,
    actionType: PropTypes.oneOf(['ADD', 'DELETE', 'EDIT']),
    otherFields: PropTypes.shape({
      cropName: PropTypes.string,
      yieldName: PropTypes.string
    })
  }).isRequired,
  onRetry: PropTypes.func.isRequired,
  onDismiss: PropTypes.func
};

export default FailedBanner;
